import { useEffect, useState } from "react";
import { toast } from "@/lib/toast";
import { AlertTriangle, RefreshCw, RotateCcw, Clock, Mail, CheckCircle2, XCircle } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import DeskConfirm from "@/components/crm/DeskConfirm";
import api from "@/lib/api";

/**
 * Spec iter75 (2026-02-XX) — Onglet Incidents dans l'historique de connexion.
 *
 * Chaque incident détecté par le backend (≥ 3 échecs OTP en 10 min) est
 * exposé ici sous forme de carte : fenêtre horaire, count, détail des
 * tentatives en échec (email + raison précise). Un bouton "Rejouer"
 * permet de renvoyer un OTP frais à chaque destinataire concerné sans
 * qu'il n'ait à recommencer sa procédure de connexion.
 *
 * Les incidents plus anciens sont visuellement estompés (opacité réduite)
 * par rapport à l'incident le plus récent.
 */

function fmtDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString("fr-FR", {
      day: "2-digit", month: "short",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

function fmtWindow(startIso, endIso) {
  try {
    const s = new Date(startIso);
    const e = new Date(endIso);
    const startStr = s.toLocaleString("fr-FR", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
    const endStr = e.toLocaleString("fr-FR", { hour: "2-digit", minute: "2-digit" });
    return `${startStr} → ${endStr}`;
  } catch {
    return "—";
  }
}

function windowDurationMinutes(startIso, endIso) {
  try {
    const s = new Date(startIso);
    const e = new Date(endIso);
    return Math.max(1, Math.round((e.getTime() - s.getTime()) / 60000));
  } catch { return 10; }
}

function ReplayButton({ incident, onReplayed }) {
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const doReplay = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/settings/otp-incidents/${incident.id}/replay`);
      const sent = data.sent?.length || 0;
      const skipped = data.skipped?.length || 0;
      if (sent > 0) {
        toast.success(`Code renvoyé à ${sent} destinataire${sent > 1 ? "s" : ""}${skipped > 0 ? ` (${skipped} ignoré${skipped > 1 ? "s" : ""})` : ""}.`);
      } else {
        toast.error("Aucun code renvoyé (destinataires introuvables ou inactifs)");
      }
      onReplayed?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de rejouer cet incident.");
    } finally {
      setBusy(false);
      setOpen(false);
    }
  };

  return (
    <>
      <Button
        size="sm"
        variant="outline"
        disabled={busy}
        onClick={() => setOpen(true)}
        className="gap-1.5 border-vertec-borderDark"
        data-testid={`incident-replay-${incident.id}`}
      >
        <RotateCcw size={13} className={busy ? "animate-spin" : ""} />
        Rejouer cet incident
      </Button>
      <DeskConfirm open={open} danger={false} busy={busy} icon={RotateCcw} title="Rejouer l'envoi des codes ?"
        message={`Un nouveau code OTP à 6 chiffres sera envoyé à chaque destinataire en échec (${incident.events?.length || 0} adresse${(incident.events?.length || 0) > 1 ? "s" : ""}). Les codes précédents seront invalidés.`}
        confirmLabel="Rejouer maintenant" onClose={() => !busy && setOpen(false)} onConfirm={doReplay} testId={`incident-replay-confirm-${incident.id}`} />
    </>
  );
}

function IncidentCard({ incident, isMostRecent, onReplayed }) {
  const events = incident.events || [];
  const duration = windowDurationMinutes(incident.window_start, incident.window_end);
  const replayed = incident.replay_count > 0;
  return (
    <Card
      className="p-5 space-y-4 border-vertec-borderDark transition-opacity"
      style={{
        borderLeft: `4px solid ${isMostRecent ? "#EF4444" : "#6B7280"}`,
        opacity: isMostRecent ? 1 : 0.62,
      }}
      data-testid={`incident-card-${incident.id}`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1.5 flex-1">
          <div className="flex items-center gap-2">
            <AlertTriangle size={18} style={{ color: isMostRecent ? "#EF4444" : "#6B7280" }} />
            <span className="font-semibold text-sm">
              Détecté le {fmtDate(incident.detected_at)}
            </span>
            {isMostRecent && (
              <span
                className="text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider"
                style={{ background: "rgba(239,68,68,0.15)", color: "#EF4444" }}
              >
                Le plus récent
              </span>
            )}
            {replayed && (
              <span
                className="text-[10px] px-2 py-0.5 rounded-full font-bold uppercase tracking-wider flex items-center gap-1"
                style={{ background: "rgba(34,197,94,0.15)", color: "#22C55E" }}
              >
                <CheckCircle2 size={10} /> Rejoué {incident.replay_count > 1 ? `×${incident.replay_count}` : ""}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <span className="flex items-center gap-1">
              <Clock size={12} /> {fmtWindow(incident.window_start, incident.window_end)}
            </span>
            <span>·</span>
            <span>{incident.failure_count} échec{incident.failure_count > 1 ? "s" : ""} en {duration} min</span>
          </div>
        </div>
        <ReplayButton incident={incident} onReplayed={onReplayed} />
      </div>

      <div className="rounded-md border border-vertec-borderDark overflow-hidden">
        <div className="grid grid-cols-[1fr_1fr] text-[11px] uppercase tracking-wider text-muted-foreground px-3 py-2 bg-background/40 border-b border-vertec-borderDark">
          <div className="flex items-center gap-1.5"><Mail size={11} /> Destinataire</div>
          <div className="flex items-center gap-1.5"><XCircle size={11} /> Cause de l&apos;échec</div>
        </div>
        <div className="divide-y divide-vertec-borderDark">
          {events.map((evt, i) => (
            <div key={evt.id || i} className="grid grid-cols-[1fr_1fr] px-3 py-2 text-sm" data-testid={`incident-event-${incident.id}-${i}`}>
              <div className="font-mono text-xs text-foreground/90 truncate" title={evt.to}>{evt.to}</div>
              <div className="text-xs text-muted-foreground truncate" title={evt.reason || evt.status}>
                {evt.reason || (evt.status === "timeout" ? "timeout" : "échec inconnu")}
              </div>
            </div>
          ))}
        </div>
      </div>

      {incident.last_replayed_at && (
        <div className="text-[11px] text-muted-foreground pl-1">
          Dernier rejeu : {fmtDate(incident.last_replayed_at)}
        </div>
      )}
    </Card>
  );
}

export default function OtpIncidentsPanel() {
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  const load = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.get("/settings/otp-incidents");
      setIncidents(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Impossible de charger les incidents.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => { load(); }, []);

  if (loading) {
    return <div className="text-muted-foreground text-sm py-10 text-center">Chargement des incidents…</div>;
  }

  if (incidents.length === 0) {
    return (
      <div className="text-muted-foreground text-sm py-10 text-center border border-dashed border-vertec-borderDark rounded-md">
        <CheckCircle2 size={28} className="inline-block mb-2 text-status-success" />
        <div>Aucun incident détecté.</div>
        <div className="text-xs mt-1">Tous les envois OTP réussissent normalement.</div>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="otp-incidents-panel">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">
          {incidents.length} incident{incidents.length > 1 ? "s" : ""} — l&apos;incident le plus récent est mis en avant, les précédents sont estompés.
        </p>
        <Button
          size="sm"
          variant="ghost"
          onClick={load}
          disabled={refreshing}
          data-testid="incidents-refresh"
        >
          <RefreshCw size={13} className={refreshing ? "animate-spin" : ""} />
          <span className="ml-1.5">Actualiser</span>
        </Button>
      </div>
      {incidents.map((inc, i) => (
        <IncidentCard
          key={inc.id}
          incident={inc}
          isMostRecent={i === 0}
          onReplayed={load}
        />
      ))}
    </div>
  );
}
